/**
 * Display formatting. One implementation each, because there were five byte formatters and four
 * relative-time formatters, and they disagreed with each other in user-visible ways.
 */

const BYTE_UNITS = ['KiB', 'MiB', 'GiB', 'TiB'] as const;

/**
 * Human-readable size using BINARY units.
 *
 * The labels are `KiB`/`MiB` rather than `KB`/`MB` because every previous copy divided by 1024 and
 * then labelled the result `KB` - so `VaultPage` advertised its 24 MiB inline cap as "24.0 MB",
 * which is simply the wrong number. Three of the five copies also stopped at MB, meaning a 3 GB
 * sample rendered as "3072.0 MB".
 */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${Math.round(n)} B`;
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024;
    i += 1;
  }
  // One decimal below 10 (2.4 MiB), two above (24.15 MiB) reads better than a fixed precision.
  return `${v.toFixed(v < 10 ? 1 : 2)} ${BYTE_UNITS[i]}`;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

/** Seconds since `iso`, or null when it is missing or unparseable. */
function ageSeconds(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  // Three of the four previous copies skipped this check and rendered "NaNd ago" on a bad
  // timestamp - which is worse than saying nothing, because it looks like a real answer.
  if (!Number.isFinite(t)) return null;
  return Math.max(0, (Date.now() - t) / 1000);
}

/**
 * Relative time in prose, for body copy: "just now", "3m ago", "5h ago", "2d ago". Older than 30
 * days falls back to an absolute date, because "412d ago" is not information anyone can use.
 */
export function timeAgo(iso: string | null | undefined): string {
  const s = ageSeconds(iso);
  if (s === null) return 'unknown';
  if (s < MINUTE) return 'just now';
  if (s < HOUR) return `${Math.floor(s / MINUTE)}m ago`;
  if (s < DAY) return `${Math.floor(s / HOUR)}h ago`;
  if (s < 30 * DAY) return `${Math.floor(s / DAY)}d ago`;
  return new Date(iso as string).toLocaleDateString();
}

/**
 * The same thing without the "ago", for dense surfaces - notification rows, table cells - where the
 * column heading already says it is an age.
 */
export function timeAgoShort(iso: string | null | undefined): string {
  const s = ageSeconds(iso);
  if (s === null) return '-';
  if (s < MINUTE) return 'now';
  if (s < HOUR) return `${Math.floor(s / MINUTE)}m`;
  if (s < DAY) return `${Math.floor(s / HOUR)}h`;
  return `${Math.floor(s / DAY)}d`;
}
